import React, { useState, useEffect, useRef } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { MenuBuilderProvider, useMenuBuilder } from '../context/MenuBuilderContext';
import { useChannel } from '../context/ChannelContext';
import { 
  RichMenuCanvas, 
  CoordinateDisplay, 
  ActionEditor,
  ImageUploader,
  MenuPreview 
} from '../components/rich-menu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Save,
  Rocket,
  ArrowLeft,
  Trash2,
  Settings2,
  Image,
  MousePointer,
  Eye,
  AlertTriangle,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { createMenu, updateMenu, getMenu, publishMenu, uploadImage } from '../services/api';

const snapshotOf = (data) => JSON.stringify({ ...data, pendingImageFile: !!data.pendingImageFile });

const MenuBuilderContent = () => {
  const navigate = useNavigate();
  const { id: menuId } = useParams();
  const { channels, selectedChannelId, selectChannel } = useChannel();
  const { menuData, updateMenuData, loadMenu, clearAreas, resetMenu } = useMenuBuilder();
  
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isReplaced, setIsReplaced] = useState(false);
  const [currentMenuId, setCurrentMenuId] = useState(menuId || null);
  const [activeTab, setActiveTab] = useState('settings');
  // The channel this menu belongs to, fixed when the editor opens: a new menu takes the channel
  // selected at that moment, an existing one its own. Switching channel never moves it.
  const [menuChannelId, setMenuChannelId] = useState(menuId ? null : selectedChannelId);
  const menuChannelName = channels.find(c => c.id === menuChannelId)?.name;
  // Unsaved changes = the menu differs from what was last loaded or saved
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const leaving = useRef(false);
  const [leaveSaving, setLeaveSaving] = useState(false);

  // Load existing menu if editing
  useEffect(() => {
    if (menuId) {
      const fetchMenu = async () => {
        try {
          const menu = await getMenu(menuId);
          setSavedSnapshot(snapshotOf(loadMenu(menu)));
          setIsReplaced(menu.status === 'replaced');
          setCurrentMenuId(menuId);
          setMenuChannelId(menu.channelId);
          // Opened by link or bookmark: the sidebar follows the menu's channel
          if (menu.channelId && menu.channelId !== selectedChannelId) selectChannel(menu.channelId);
        } catch (error) {
          toast.error('Failed to load menu');
          navigate('/menus');
        }
      };
      fetchMenu();
    } else {
      setSavedSnapshot(snapshotOf(resetMenu()));
      setIsReplaced(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuId]);

  // A new menu opened before the channels loaded takes the channel once one is selected
  useEffect(() => {
    if (!menuId && !menuChannelId && selectedChannelId) setMenuChannelId(selectedChannelId);
  }, [menuId, menuChannelId, selectedChannelId]);

  const hasChanges = savedSnapshot !== null && snapshotOf(menuData) !== savedSnapshot;

  // Leaving the editor in any way (sidebar, back button, channel switch) asks to save first
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    hasChanges && !leaving.current && currentLocation.pathname !== nextLocation.pathname);

  // Closing or reloading the tab can only get the browser's own warning
  useEffect(() => {
    if (!hasChanges) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasChanges]);

  // Core save logic — throws on error, returns the saved menu id
  const _performSave = async ({ fallbackName } = {}) => {
    const name = menuData.name?.trim() ? menuData.name : fallbackName;
    if (!name?.trim()) {
      // Focus the name input so the user knows what to fix
      document.querySelector('[data-testid="menu-name-input"]')?.focus();
      throw new Error('Please enter a menu name before saving');
    }
    let imageUrl = menuData.imageUrl;
    if (menuData.pendingImageFile) {
      const result = await uploadImage(menuData.pendingImageFile);
      if (imageUrl?.startsWith('blob:')) URL.revokeObjectURL(imageUrl);
      imageUrl = result.url;
      updateMenuData({ imageUrl, pendingImageFile: null });
    }

    const payload = {
      name,
      chatBarText: menuData.chatBarText,
      size: menuData.size,
      areas: menuData.areas,
      selected: menuData.selected,
      imageUrl,
      richMenuAliasId: menuData.richMenuAliasId || null,
      channelId: menuChannelId
    };

    let savedId = currentMenuId;
    if (currentMenuId) {
      await updateMenu(currentMenuId, payload);
    } else {
      const result = await createMenu(payload);
      savedId = result.id;
      setCurrentMenuId(result.id);
      window.history.replaceState(null, '', `/edit/${result.id}`);
    }
    if (name !== menuData.name) updateMenuData({ name });
    setSavedSnapshot(snapshotOf({ ...menuData, name, imageUrl, pendingImageFile: null }));
    return savedId;
  };

  const handleSave = async () => {
    if (!menuChannelId && !currentMenuId) {
      toast.error('Please select a channel first');
      return;
    }
    setSaving(true);
    try {
      const isNew = !currentMenuId;
      await _performSave();
      toast.success(isNew ? 'Menu created' : 'Menu saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to save menu');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!menuData.imageUrl) {
      toast.error('Please upload an image before publishing');
      return;
    }
    if (menuData.areas.length === 0) {
      toast.error('Please add at least one tap area');
      return;
    }

    setPublishing(true);
    const publishFlow = async () => {
      const menuId = await _performSave();
      await publishMenu(menuId);
    };

    toast.promise(publishFlow(), {
      loading: 'Publishing to LINE...',
      success: () => {
        setPublishing(false);
        leaving.current = true;
        navigate('/menus');
        return 'Menu published to LINE!';
      },
      error: (err) => {
        setPublishing(false);
        return err.response?.data?.detail || 'Failed to publish menu';
      },
    });
  };

  const handleSizeChange = (size) => {
    const sizes = {
      full: { width: 2500, height: 1686 },
      compact: { width: 2500, height: 843 }
    };
    updateMenuData({ size: sizes[size] });
    clearAreas();
    toast.info('Areas cleared due to size change');
  };

  const handleLeaveSave = async () => {
    setLeaveSaving(true);
    try {
      await _performSave({ fallbackName: 'Untitled menu' });
      blocker.proceed?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to save menu');
      blocker.reset?.();
    } finally {
      setLeaveSaving(false);
    }
  };

  const isCompact = menuData.size.height === 843;

  // No channel selected - show warning for new menus
  if (!menuChannelId && !menuId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-[#18181B] flex items-center justify-center mb-4">
          <AlertTriangle className="w-8 h-8 text-yellow-500" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No channel selected</h3>
        <p className="text-[#A1A1AA] text-sm mb-6 max-w-sm">
          Please select a channel from the sidebar before creating a menu
        </p>
        <Button 
          variant="outline"
          onClick={() => navigate('/menus')}
          className="border-[#27272A]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Menus
        </Button>
      </div>
    );
  }

  // Settings panel content - inline to prevent focus loss
  const settingsPanelContent = (
    <div className="space-y-4">
      <div>
        <Label className="text-xs text-[#A1A1AA]">Chat Bar Text</Label>
        <Input
          value={menuData.chatBarText}
          onChange={(e) => updateMenuData({ chatBarText: e.target.value })}
          maxLength={14}
          className="bg-[#18181B] border-[#27272A] mt-1"
          data-testid="chat-bar-text-input"
        />
        <p className="text-xs text-[#52525B] mt-1">{menuData.chatBarText.length}/14</p>
      </div>

      <div>
        <Label className="text-xs text-[#A1A1AA]">Menu Size</Label>
        <Select
          value={isCompact ? 'compact' : 'full'}
          onValueChange={handleSizeChange}
        >
          <SelectTrigger 
            className="bg-[#18181B] border-[#27272A] mt-1"
            data-testid="size-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#18181B] border-[#27272A]">
            <SelectItem value="full" className="focus:bg-[#27272A]">
              Full (2500×1686)
            </SelectItem>
            <SelectItem value="compact" className="focus:bg-[#27272A]">
              Compact (2500×843)
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs text-[#A1A1AA]">Menu Alias ID</Label>
        <Input
          value={menuData.richMenuAliasId || ''}
          onChange={(e) => updateMenuData({ richMenuAliasId: e.target.value })}
          placeholder="e.g., richmenu-alias-main"
          className="bg-[#18181B] border-[#27272A] mt-1 font-mono text-sm"
          data-testid="alias-id-input"
        />
        {menuData.richMenuAliasId && !/^[a-zA-Z0-9_-]{1,32}$/.test(menuData.richMenuAliasId) ? (
          <p className="text-xs text-red-400 mt-1">Letters, numbers, '-' and '_' only (max 32)</p>
        ) : (
          <p className="text-xs text-[#52525B] mt-1">Used for richmenuswitch actions. Keep the same alias when replacing a live menu.</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full" data-testid="menu-builder">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border-b border-[#27272A] gap-3">
        <div className="flex items-center gap-3 sm:gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/menus')}
            data-testid="back-btn"
            className="flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="relative group">
              <Input
                value={menuData.name}
                onChange={(e) => updateMenuData({ name: e.target.value })}
                placeholder="Enter menu name…"
                autoFocus={!menuId}
                className="text-lg sm:text-xl font-bold bg-[#18181B] border-[#3F3F46] pr-8 focus-visible:border-[#00E05E] focus-visible:ring-0 transition-colors"
                data-testid="menu-name-input"
              />
              <Pencil className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#52525B] pointer-events-none group-focus-within:text-[#00E05E] transition-colors" />
            </div>
            <p className="text-xs text-[#A1A1AA] mt-1" data-testid="menu-channel">
              {currentMenuId ? 'Editing' : 'New menu'}
              {menuChannelName && <> in <span className="text-[#06C755]">{menuChannelName}</span></>}
              {hasChanges && <span className="text-[#52525B]"> · Unsaved changes</span>}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={handleSave}
            disabled={saving}
            className="border-[#27272A] flex-1 sm:flex-none"
            data-testid="save-btn"
          >
            <Save className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save'}</span>
          </Button>
          <Button
            className="btn-publish flex-1 sm:flex-none"
            onClick={handlePublish}
            disabled={isReplaced || publishing || !menuData.imageUrl || menuData.areas.length === 0}
            data-testid="publish-btn"
          >
            <Rocket className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{publishing ? 'Publishing...' : 'Save & Publish'}</span>
          </Button>
        </div>
      </div>

      <AlertDialog open={blocker.state === 'blocked'} onOpenChange={(open) => { if (!open && !leaveSaving) blocker.reset?.(); }}>
        <AlertDialogContent className="bg-[#18181B] border-[#27272A]" data-testid="unsaved-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Save your changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              {menuData.name?.trim() ? <>“{menuData.name}”</> : 'This menu'} will be saved as a draft in{' '}
              <span className="text-[#06C755]">{menuChannelName || 'its channel'}</span>. It won't go live on LINE until you publish it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-[#27272A]"
              disabled={leaveSaving}
              onClick={() => blocker.reset?.()}
              data-testid="unsaved-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              className="border-[#27272A]"
              disabled={leaveSaving}
              onClick={() => blocker.proceed?.()}
              data-testid="unsaved-discard"
            >
              Don't save
            </Button>
            <Button
              className="btn-primary"
              disabled={leaveSaving}
              onClick={handleLeaveSave}
              data-testid="unsaved-save"
            >
              {leaveSaving ? 'Saving...' : 'Save draft'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isReplaced && (
        <div
          className="px-4 py-2 text-sm bg-yellow-500/10 border-b border-yellow-500/30 text-yellow-500"
          data-testid="replaced-banner"
        >
          This menu was replaced by a campaign launch. Edits won't go live — duplicate it to reuse it.
        </div>
      )}

      {/* Mobile tabs view */}
      <div className="lg:hidden flex-1 overflow-y-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col min-h-full">
          <TabsList className="grid grid-cols-4 mx-3 mt-3 bg-[#18181B] sticky top-0 z-10 shrink-0">
            <TabsTrigger value="settings" className="flex flex-col gap-0.5 text-[10px] py-1.5 data-[state=active]:bg-[#27272A]">
              <Settings2 className="w-4 h-4" />
              <span>Settings</span>
            </TabsTrigger>
            <TabsTrigger value="image" className="flex flex-col gap-0.5 text-[10px] py-1.5 data-[state=active]:bg-[#27272A]">
              <Image className="w-4 h-4" />
              <span>Image</span>
            </TabsTrigger>
            <TabsTrigger value="canvas" className="flex flex-col gap-0.5 text-[10px] py-1.5 data-[state=active]:bg-[#27272A]">
              <MousePointer className="w-4 h-4" />
              <span>Canvas</span>
            </TabsTrigger>
            <TabsTrigger value="action" className="flex flex-col gap-0.5 text-[10px] py-1.5 data-[state=active]:bg-[#27272A]">
              <Eye className="w-4 h-4" />
              <span>Preview</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="flex-1 p-3">
            <div className="glass-panel rounded-lg p-4">
              <p className="label-text mb-3">Menu Settings</p>
              {settingsPanelContent}
            </div>
          </TabsContent>

          <TabsContent value="image" className="flex-1 p-3">
            <ImageUploader />
          </TabsContent>

          <TabsContent value="canvas" className="flex-1 p-3 bg-[#0a0a0b]">
            <div className="flex items-center justify-between mb-3">
              <p className="label-text">Canvas</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAreas}
                className="text-[#52525B] hover:text-red-400 text-xs"
                data-testid="clear-areas-btn"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Clear
              </Button>
            </div>
            <RichMenuCanvas />
            <p className="text-xs text-[#52525B] mt-3 mb-4 text-center">
              Tap and drag to create areas
            </p>
            <ActionEditor />
          </TabsContent>

          <TabsContent value="action" className="flex-1 p-3">
            <MenuPreview />
          </TabsContent>
        </Tabs>
      </div>

      {/* Desktop layout */}
      <div className="hidden lg:flex flex-1 overflow-hidden">
        {/* Left sidebar - Tools */}
        <div className="w-64 xl:w-72 border-r border-[#27272A] p-4 overflow-y-auto space-y-4 flex-shrink-0">
          {/* Menu Settings */}
          <div className="glass-panel rounded-lg p-4">
            <p className="label-text mb-3">Menu Settings</p>
            {settingsPanelContent}
          </div>

          <ImageUploader />
          
          <CoordinateDisplay />
        </div>

        {/* Center - Canvas */}
        <div className="flex-1 p-4 xl:p-6 overflow-auto bg-[#0a0a0b] min-w-0">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <p className="label-text">Canvas</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAreas}
                className="text-[#52525B] hover:text-red-400"
                data-testid="clear-areas-btn-desktop"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            </div>
            
            <RichMenuCanvas />
            
            <p className="text-xs text-[#52525B] mt-4 text-center">
              Click and drag on the canvas to create tap areas. Click an area to select and edit it.
            </p>
          </div>
        </div>

        {/* Right sidebar - Properties */}
        <div className="w-64 xl:w-72 border-l border-[#27272A] p-4 overflow-y-auto space-y-4 flex-shrink-0">
          <ActionEditor />
          <MenuPreview />
        </div>
      </div>
    </div>
  );
};

export const MenuBuilder = () => {
  return (
    <MenuBuilderProvider>
      <MenuBuilderContent />
    </MenuBuilderProvider>
  );
};

export default MenuBuilder;
